/**
 * CreationLeaderboard — the per-creation RANKED ladder on a game's landing
 * page. Every creation whose play-mode set includes `onlineVersus` owns its own
 * Elo ladder (gameId = creationId): the game server's trusted writer records
 * ranked results under `games/{creationId}/…` and the scheduled recompute folds
 * them into the single snapshot doc at `games/{creationId}/leaderboard/snapshot`.
 *
 * Reads through the platform's ONE leaderboard read path —
 * `@djgg/leaderboard`'s {@link LeaderboardSnapshotReader} over the portal's doc
 * store (`services.repos.leaderboard`) — which distinguishes a genuinely empty
 * board from a FAILED read: a signed-out viewer's denied snapshot read renders
 * a sign-in hint, never the misleading "no matches yet" empty state.
 */
import { useEffect, useState } from 'react';
import type { LeaderboardLoadResult } from '@djgg/leaderboard';
import { useServices } from '../services/context.js';

export function CreationLeaderboard({
  creationId,
  expectedKind,
}: {
  creationId: string;
  /**
   * The board kind the caller already knows this game has, used ONLY until a
   * snapshot exists. An unplayed board mints no snapshot by design, so without
   * this a solo creation would fall back to the Elo default and invite the
   * player to a "Play online" duel it has no mode for. Natives always have a
   * snapshot (their recompute runs whether or not anyone has played), so the
   * caller can leave this unset for them.
   */
  expectedKind?: 'elo' | 'highscore';
}) {
  const services = useServices();
  const [result, setResult] = useState<LeaderboardLoadResult | null>(null);

  useEffect(() => {
    let active = true;
    setResult(null); // component instances are reused across game→game navigation
    services.repos.leaderboard
      .fetchResult(creationId)
      .then((r) => {
        if (active) setResult(r);
      })
      .catch(() => {
        // fetchResult classifies failures itself; this is the belt for a throw
        // outside its load path (e.g. an invalid id).
        if (active) setResult({ rows: [], error: 'unknown', computedAtMs: null, kind: null });
      });
    return () => {
      active = false;
    };
  }, [services, creationId]);

  // A board is an Elo ladder or a career-best high-score table, and the two say
  // different things — the recompute stamps which onto the snapshot so this
  // never has to guess (or import a descriptor the portal may not reach).
  const highscore = (result?.kind ?? expectedKind ?? 'elo') === 'highscore';

  return (
    <div className="panel" data-testid="creation-leaderboard" data-board-kind={result?.kind ?? expectedKind ?? ''}>
      <h2 className="panel__title">{highscore ? '🏆 High scores' : '⚔ Ranked ladder'}</h2>
      {result === null && <p className="muted">Loading…</p>}
      {result?.error === 'denied' && (
        <p className="muted" data-testid="leaderboard-denied">
          Sign in to see this game’s {highscore ? 'high scores' : 'ladder'}.
        </p>
      )}
      {(result?.error === 'offline' || result?.error === 'unknown') && (
        <p className="error-text" data-testid="leaderboard-error">
          Couldn’t load the board — try again in a moment.
        </p>
      )}
      {result?.error === null && result.rows.length === 0 && (
        <p className="muted" data-testid="leaderboard-empty">
          {highscore
            ? 'No scores yet — finish a run signed in to put yourself on the board.'
            : 'No ranked matches yet — the first “Play online” duel starts the ladder.'}
        </p>
      )}
      {result?.error === null && result.rows.length > 0 && (
        <div className="table__scroll">
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Player</th>
                <th>{highscore ? 'Best score' : 'Rating'}</th>
                <th>{highscore ? 'Runs' : 'Games'}</th>
              </tr>
            </thead>
            <tbody>
              {result.rows.map((r) => (
                <tr key={r.uid} data-testid="leaderboard-row">
                  <td>{r.rank}</td>
                  <td>{r.nickname}</td>
                  <td>{r.rating}</td>
                  <td>{r.gamesPlayed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
